import { useState } from 'react';
import Button from '@/components/Button';
import { Customer, CustomerAddOrUpdate } from '@/modules/supplier/models/Customer';
import { withLoading, withMinimumDelay } from '@/utils/async';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Controller, useForm } from 'react-hook-form';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (customer: CustomerAddOrUpdate) => Promise<void>;
}

const CustomerForm = ({ customer, onSubmit }: CustomerFormProps) => {
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CustomerAddOrUpdate>({
    defaultValues: customer
      ? { ...customer }
      : {
          id: '',
          name: '',
        },
  });

  const onSubmitForm = withLoading(
    withMinimumDelay(async (data: CustomerAddOrUpdate) => {
      try {
        await onSubmit(data);
      } catch (err) {
        console.error('Submission error:', err);
      }
    }, 500),
    setLoading,
  );

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div className="grid grid-cols-2 gap-4 lg:gap-6">
        <div className="col-span-2">
          <label htmlFor="name" className="block font-medium text-gray-700">
            Name:
          </label>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <input
                id="name"
                {...field}
                className="mt-1 p-2 border border-gray-300 rounded-md w-full"
              />
            )}
          />
          {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
        </div>

        <div className="col-span-2">
          <label htmlFor="customerCode" className="block font-medium text-gray-700">
            Code:
          </label>
          <Controller
            name="customerCode"
            control={control}
            render={({ field }) => (
              <input
                id="externalCode"
                {...field}
                className="mt-1 p-2 border border-gray-300 rounded-md w-full"
              />
            )}
          />
          {errors.customerCode && (
            <p className="text-red-500 text-sm">{errors.customerCode.message}</p>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
          loading={loading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default CustomerForm;
